import './styles/globals.css';
import './styles/responsive.css';
import { Header, VersionInfo } from './components/layout';
import { 
  MainPage, 
  ClientManagement, 
  QualityAssurance, 
  Analysis, 
  SampleControl, 
  MyDashboard, 
  Configurations, 
  NotFoundPage,
  DatabaseAssistant,
  ClientOnboardingWelcome,
  ClientOnboardingTerms,
  ClientOnboardingAccountType,
  ClientOnboardingBusinessInfo,
  ClientOnboardingBillingReporting,
  ClientOnboardingReportingRequirements,
  ClientOnboardingQCDisposal,
  ClientOnboardingReview,
  ClientOnboardingForm,
  ClientOnboardingSuccess
} from './pages';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AlertContainer } from './components/ui';
import { PrivateRoute, AutoLogin, DefaultViewRedirect } from './components/auth';
import { ThemeProvider } from './context/ThemeContext';
import { BackendProvider } from './context/BackendContext';
import { createProtectedRoute, createRoute } from './utils/routeUtils';
import { logVersionInfo } from './utils/versionInfo';
import InstallPrompt from './components/ui/InstallPrompt';
import React from 'react';

/**
 * Client-facing onboarding app
 * Minimal isolated wrapper for public client onboarding
 * No authentication required - secured via token link
 */
function ClientOnboardingApp() {
  React.useEffect(() => {
    // Add class to remove header padding for public forms
    document.body.classList.add('no-header-padding', 'public-form-only');
    
    // Remove PWA manifest to prevent app installation on client-facing pages
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const originalManifestHref = manifestLink?.getAttribute('href');
    if (manifestLink) {
      manifestLink.remove();
    }
    
    // Add meta tag to prevent mobile browsers from showing install banners
    const metaTag = document.createElement('meta');
    metaTag.name = 'mobile-web-app-capable';
    metaTag.content = 'no';
    document.head.appendChild(metaTag);
    
    return () => {
      document.body.classList.remove('no-header-padding', 'public-form-only');
      
      // Restore manifest link when leaving client onboarding
      if (originalManifestHref && !document.querySelector('link[rel="manifest"]')) {
        const newManifestLink = document.createElement('link');
        newManifestLink.rel = 'manifest';
        newManifestLink.href = originalManifestHref;
        document.head.appendChild(newManifestLink);
      }
      
      // Remove the meta tag
      metaTag.remove();
    };
  }, []);

  return (
    <Routes>
      {/* Client onboarding welcome page */}
      <Route path="/client-onboarding/:token" element={<ClientOnboardingWelcome />} />
      
      {/* Client onboarding terms and conditions */}
      <Route path="/client-onboarding/:token/terms" element={<ClientOnboardingTerms />} />
      
      {/* Client onboarding account type selection */}
      <Route path="/client-onboarding/:token/account-type" element={<ClientOnboardingAccountType />} />
      
      {/* Client onboarding business information (for business accounts) */}
      <Route path="/client-onboarding/:token/business-info" element={<ClientOnboardingBusinessInfo />} />
      
      {/* Client onboarding billing and reporting contacts */}
      <Route path="/client-onboarding/:token/billing-reporting" element={<ClientOnboardingBillingReporting />} />
      
      {/* Client onboarding reporting requirements */}
      <Route path="/client-onboarding/:token/reporting-requirements" element={<ClientOnboardingReportingRequirements />} />
      
      {/* Client onboarding QC and disposal */}
      <Route path="/client-onboarding/:token/qc-disposal" element={<ClientOnboardingQCDisposal />} />
      
      {/* Client onboarding review */}
      <Route path="/client-onboarding/:token/review" element={<ClientOnboardingReview />} />
      
      {/* Client onboarding success */}
      <Route path="/client-onboarding/:token/success" element={<ClientOnboardingSuccess />} />
      
      {/* Client onboarding form page */}
      <Route path="/client-onboarding/:token/form" element={<ClientOnboardingForm />} />
      
      {/* Catch-all for public routes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Main authenticated app with all features
 */
function AuthenticatedApp() {
  React.useEffect(() => {
    logVersionInfo();
  }, []);

  // Configuration for protected routes
  const protectedRouteConfigs = [
    { path: '/samples/*', component: SampleControl },
    { path: '/lab/*', component: Analysis },
    { path: '/quality-assurance/*', component: QualityAssurance },
    { path: '/clients/*', component: ClientManagement },
    { path: '/dashboard', component: MyDashboard },
    { path: '/configurations/*', component: Configurations },
    { path: '/database-assistant', component: DatabaseAssistant }
  ];

  // Configuration for public routes
  const publicRouteConfigs = [
    { path: '/', component: MainPage }
  ];

  return (
    <ThemeProvider>
      <BackendProvider>
        <AutoLogin>
          <DefaultViewRedirect>
            <InstallPrompt />
            <div className="App">
              <div className="top-bar-stack">
                <AlertContainer />
                <Header />
              </div>
              <Routes>
                {/* Public routes */}
                {publicRouteConfigs.map(({ path, component: Component }) => 
                  createRoute(path, Component)
                )}
                
                {/* Protected routes */}
                {protectedRouteConfigs.map(({ path, component: Component }) => 
                  createProtectedRoute(path, Component)
                )}
                
                {/* Catch-all route for 404 errors */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <VersionInfo />
            </div>
          </DefaultViewRedirect>
        </AutoLogin>
      </BackendProvider>
    </ThemeProvider>
  );
}

/**
 * App router that determines which app version to load
 */
function App() {
  const location = useLocation();
  
  // Check if current route is client onboarding route
  const isClientOnboardingRoute = location.pathname.startsWith('/client-onboarding/');
  
  // Render minimal client onboarding app or full authenticated app
  return isClientOnboardingRoute ? <ClientOnboardingApp /> : <AuthenticatedApp />;
}

export default App;
